/**
 * Client admin gate — Grudge ID (id.grudge-studio.com/admin) + Puter + treasury.
 * Does not replace player auth. Aligns with GrudgeBuilder adminAllowlist.
 */
import {
  ADMIN_EMAILS,
  ADMIN_HANDLES,
  ADMIN_TREASURY_WALLET,
  isAdminEmail,
  isAdminHandle,
  isAdminTreasuryWallet,
  normalizeAdminToken,
  adminAllowlistPublic,
} from "@shared/admin-allowlist";
import {
  captureAdminHandoffFromUrl,
  captureSsoTokenFromUrl,
  getGrudgeToken,
  loginWithGrudgeAdmin,
} from "./grudge-auth";

export { ADMIN_TREASURY_WALLET, adminAllowlistPublic, loginWithGrudgeAdmin };

export type AdminIdentityHints = {
  email?: string;
  handle?: string;
  puter?: string;
  wallet?: string | null;
  /** True after returning from id.grudge-studio.com/admin */
  fromAdminGate?: boolean;
};

export type AdminSessionResolution = {
  ok: boolean;
  hints: AdminIdentityHints;
  reason?: string;
  source?: "jwt" | "puter" | "treasury" | "stored" | "none";
};

/** Best-effort Puter username (iframe or top-level puter.com apps) */
export async function detectPuterUsername(): Promise<string | null> {
  try {
    const w = window as unknown as {
      puter?: {
        auth?: {
          getUser?: () => Promise<{ username?: string; email?: string } | null>;
          isSignedIn?: () => Promise<boolean>;
        };
      };
    };
    if (!w.puter?.auth?.getUser) return null;
    const signed = w.puter.auth.isSignedIn
      ? await w.puter.auth.isSignedIn()
      : true;
    if (!signed) return null;
    const u = await w.puter.auth.getUser();
    const name = u?.username || (u as { name?: string })?.name;
    return name ? normalizeAdminToken(name) : null;
  } catch {
    return null;
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.replace(/^Bearer\s+/i, "").split(".");
    if (parts.length < 2) return null;
    const pad =
      parts[1]!.length % 4 === 0
        ? ""
        : "=".repeat(4 - (parts[1]!.length % 4));
    const json = atob(
      parts[1]!.replace(/-/g, "+").replace(/_/g, "/") + pad,
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Pull email/handle from Grudge JWT (soft decode; server re-checks allowlist). */
export function hintsFromJwt(token: string | null): AdminIdentityHints {
  if (!token) return {};
  const p = decodeJwtPayload(token);
  if (!p) return {};
  const email =
    (p.email as string) ||
    (p.user_email as string) ||
    (typeof p.preferred_username === "string" &&
    String(p.preferred_username).includes("@")
      ? (p.preferred_username as string)
      : undefined);
  const handle =
    (p.username as string) ||
    (p.handle as string) ||
    (p.preferred_username as string) ||
    (typeof p.sub === "string" && !String(p.sub).includes("@")
      ? (p.sub as string)
      : undefined);
  const puter =
    (p.puter_username as string) || (p.puter as string) || undefined;
  return {
    email: email ? normalizeAdminToken(email) : undefined,
    handle: handle ? normalizeAdminToken(handle) : undefined,
    puter: puter ? normalizeAdminToken(puter) : undefined,
  };
}

export function adminHeaders(
  hints: AdminIdentityHints,
): Record<string, string> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (hints.wallet) h["X-Wallet-Address"] = hints.wallet;
  if (hints.email) h["X-Admin-Email"] = hints.email;
  if (hints.handle) h["X-Admin-Handle"] = hints.handle;
  if (hints.puter) h["X-Puter-Username"] = hints.puter;
  try {
    const t = getGrudgeToken();
    if (t) h.Authorization = `Bearer ${t}`;
  } catch {
    /* ignore */
  }
  try {
    if (hints.handle)
      localStorage.setItem("poker_admin_handle", hints.handle);
    if (hints.email) localStorage.setItem("poker_admin_email", hints.email);
    if (hints.puter) localStorage.setItem("poker_admin_puter", hints.puter);
  } catch {
    /* ignore */
  }
  return h;
}

export function loadStoredAdminHints(): AdminIdentityHints {
  try {
    return {
      email: localStorage.getItem("poker_admin_email") || undefined,
      handle: localStorage.getItem("poker_admin_handle") || undefined,
      puter: localStorage.getItem("poker_admin_puter") || undefined,
      fromAdminGate: localStorage.getItem("poker_admin_gate") === "1",
    };
  } catch {
    return {};
  }
}

export function clientLooksLikeAdmin(hints: AdminIdentityHints): boolean {
  return (
    isAdminEmail(hints.email) ||
    isAdminHandle(hints.handle) ||
    isAdminHandle(hints.puter) ||
    isAdminTreasuryWallet(hints.wallet)
  );
}

/**
 * Capture SSO + admin handoff, merge JWT + Puter, resolve whether operator.
 * Call once on /admin mount.
 */
export async function resolveAdminSession(
  wallet?: string | null,
): Promise<AdminSessionResolution> {
  captureSsoTokenFromUrl();
  const handoff = captureAdminHandoffFromUrl();
  const token = getGrudgeToken();
  const jwtHints = hintsFromJwt(token);
  const puterLive = await detectPuterUsername();
  const stored = loadStoredAdminHints();

  const hints: AdminIdentityHints = {
    email: jwtHints.email || stored.email,
    handle:
      jwtHints.handle ||
      handoff.puter ||
      puterLive ||
      stored.handle ||
      stored.puter,
    puter: handoff.puter || puterLive || jwtHints.puter || stored.puter,
    wallet: wallet || undefined,
    fromAdminGate: handoff.adminGate || stored.fromAdminGate,
  };

  if (isAdminTreasuryWallet(hints.wallet)) {
    return { ok: true, hints, source: "treasury" };
  }
  if (isAdminEmail(hints.email) || isAdminHandle(hints.handle)) {
    return {
      ok: true,
      hints,
      source: jwtHints.email || jwtHints.handle ? "jwt" : "stored",
    };
  }
  if (isAdminHandle(hints.puter)) {
    return { ok: true, hints, source: "puter" };
  }

  return {
    ok: false,
    hints,
    source: "none",
    reason:
      "Sign in at id.grudge-studio.com/admin as molochdadev or grudachain (or fleet admin email). Player logins cannot open /admin.",
  };
}

export function describeAllowlist(): string {
  return `id.grudge-studio.com/admin · Puter ${ADMIN_HANDLES.slice(0, 2).join(" / ")} · ${ADMIN_EMAILS.join(", ")} · treasury ${ADMIN_TREASURY_WALLET.slice(0, 8)}…`;
}
